import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { 
  Calendar, 
  Clock, 
  User, 
  Users,
  Wrench,
  CheckCircle,
  Truck,
  Package,
  AlertCircle,
  Filter,
  UserX,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MyAssignments = () => {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [rentalAssignments, setRentalAssignments] = useState([]);
  const [teamData, setTeamData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [statusFilter, setStatusFilter] = useState("active");

  const isManagerOrAdmin = currentUser?.role === "admin" || currentUser?.role === "manager";

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      const [assignmentsRes, userRes] = await Promise.all([
        axios.get(`${API}/my/assignments`, { withCredentials: true }),
        axios.get(`${API}/auth/me`, { withCredentials: true }),
      ]);
      setAssignments(assignmentsRes.data);
      setCurrentUser(userRes.data);
      
      // Fetch rental assignments
      if (userRes.data?.user_id) {
        try {
          const rentalsRes = await axios.get(`${API}/my/rental-assignments`, { withCredentials: true });
          setRentalAssignments(rentalsRes.data);
        } catch (err) {
          console.log("Rental assignments not available");
        }
      }

      // Fetch team data for admin/manager
      if (userRes.data?.role === "admin" || userRes.data?.role === "manager") {
        try {
          const teamRes = await axios.get(`${API}/team/assignments?status_filter=${statusFilter}`, { withCredentials: true });
          setTeamData(teamRes.data);
        } catch (err) {
          console.log("Team assignments not available");
        }
      }
    } catch (error) {
      toast.error("Failed to load assignments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssignments();
  }, [statusFilter]);

  const getStatusBadge = (status) => {
    const styles = {
      pending: { variant: "outline", label: "Pending" },
      confirmed: { variant: "default", label: "Confirmed" },
      completed: { variant: "secondary", label: "Completed" },
      cancelled: { variant: "destructive", label: "Cancelled" },
    };
    const style = styles[status] || { variant: "secondary", label: status };
    return <Badge variant={style.variant}>{style.label}</Badge>;
  };

  const getRentalStatusBadge = (status) => {
    const styles = {
      pending_delivery: { bg: "bg-amber-100 text-amber-800", label: "Pending Delivery" },
      delivered: { bg: "bg-blue-100 text-blue-800", label: "Delivered" },
      pending_return: { bg: "bg-purple-100 text-purple-800", label: "Pending Return" },
      return_scheduled: { bg: "bg-purple-100 text-purple-800", label: "Return Scheduled" },
      approved: { bg: "bg-green-100 text-green-800", label: "Approved" },
      reserved: { bg: "bg-slate-100 text-slate-800", label: "Reserved" },
      returned: { bg: "bg-emerald-100 text-emerald-800", label: "Returned" },
      completed: { bg: "bg-emerald-100 text-emerald-800", label: "Completed" },
    };
    const style = styles[status] || { bg: "bg-slate-100 text-slate-700", label: status?.replace(/_/g, " ") };
    return <Badge className={`${style.bg} rounded-sm`}>{style.label}</Badge>;
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: { bg: "bg-red-100 text-red-700", label: "Admin" },
      manager: { bg: "bg-purple-100 text-purple-700", label: "Manager" },
      worker: { bg: "bg-blue-100 text-blue-700", label: "Technician" },
    };
    const style = styles[role] || { bg: "bg-slate-100 text-slate-700", label: role };
    return <Badge className={`${style.bg} rounded-sm text-xs`}>{style.label}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-ZA', { 
      weekday: 'short', 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const isToday = (dateStr) => {
    if (!dateStr) return false;
    const today = new Date().toISOString().split('T')[0];
    return dateStr === today;
  };

  const isTomorrow = (dateStr) => {
    if (!dateStr) return false;
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return dateStr === tomorrow.toISOString().split('T')[0];
  };

  // Stats for worker view
  const todayAssignments = assignments.filter(a => isToday(a.booking_date));
  const confirmedCount = assignments.filter(a => a.status === "confirmed").length;
  const pendingDeliveries = rentalAssignments.filter(r => ["pending_delivery", "approved", "reserved"].includes(r.status));
  const todayRentals = rentalAssignments.filter(r => isToday(r.start_date));

  // Get my jobs from team data (for admin/manager)
  const myServiceJobs = teamData?.workers?.find(w => w.worker_id === currentUser?.user_id)?.service || [];
  const myRentalJobs = teamData?.workers?.find(w => w.worker_id === currentUser?.user_id)?.rental || [];
  const otherWorkers = teamData?.workers?.filter(w => w.worker_id !== currentUser?.user_id) || [];

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  // Render job table component
  const JobTable = ({ jobs, type, showWorker = false }) => {
    if (!jobs || jobs.length === 0) {
      return (
        <div className="text-center py-4 text-slate-400 text-sm">
          No {type} jobs
        </div>
      );
    }

    if (type === "service") {
      return (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Time</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Service</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {jobs.map((job) => (
              <TableRow 
                key={job.booking_id}
                className={`cursor-pointer hover:bg-blue-50 ${isToday(job.booking_date) ? "bg-blue-50/50" : ""}`}
                onClick={() => navigate(`/job-card/${job.booking_id}`)}
              >
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-slate-400" />
                    <div>
                      <div className="font-medium text-sm">{formatDate(job.booking_date)}</div>
                      {isToday(job.booking_date) && <span className="text-xs text-blue-600 font-medium">Today</span>}
                      {isTomorrow(job.booking_date) && <span className="text-xs text-orange-600 font-medium">Tomorrow</span>}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1 text-sm">
                    <Clock className="h-3 w-3 text-slate-400" />
                    {job.time_slot?.start_time} - {job.time_slot?.end_time}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="font-medium text-sm">{job.customer_name}</div>
                  <div className="text-xs text-slate-500">{job.installation_name}</div>
                </TableCell>
                <TableCell className="text-sm">{job.service_name || "-"}</TableCell>
                <TableCell>{getStatusBadge(job.status)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      );
    }

    // Rental type
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Delivery Date</TableHead>
            <TableHead>Customer</TableHead>
            <TableHead>Item</TableHead>
            <TableHead>Qty</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {jobs.map((job) => (
            <TableRow 
              key={job.rental_id}
              className={`cursor-pointer hover:bg-amber-50 ${isToday(job.start_date) ? "bg-amber-50/50" : ""}`}
              onClick={() => navigate(`/rental-job-card/${job.rental_id}`)}
            >
              <TableCell>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-slate-400" />
                  <div>
                    <div className="font-medium text-sm">{formatDate(job.start_date)}</div>
                    {isToday(job.start_date) && <span className="text-xs text-amber-600 font-medium">Today</span>}
                    {isTomorrow(job.start_date) && <span className="text-xs text-orange-600 font-medium">Tomorrow</span>}
                  </div>
                </div>
              </TableCell>
              <TableCell className="font-medium text-sm">{job.customer_name}</TableCell>
              <TableCell>
                <div className="flex items-center gap-2 text-sm">
                  <Package className="h-3 w-3 text-slate-400" />
                  {job.item_name}
                </div>
              </TableCell>
              <TableCell>{job.quantity}</TableCell>
              <TableCell>{getRentalStatusBadge(job.status)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  // Worker section component
  const WorkerSection = ({ worker }) => {
    const hasJobs = worker.service.length > 0 || worker.rental.length > 0;
    if (!hasJobs) return null;

    return (
      <Card className="mb-4">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center">
                <User className="h-5 w-5 text-slate-600" />
              </div>
              <div>
                <CardTitle className="text-base">{worker.worker_name}</CardTitle>
                <div className="flex items-center gap-2 mt-1">
                  {getRoleBadge(worker.worker_role)}
                  <span className="text-xs text-slate-500">
                    {worker.service.length} service, {worker.rental.length} rental
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {worker.service.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Wrench className="h-4 w-4 text-blue-500" />
                <span className="text-sm font-medium text-slate-700">Service Jobs ({worker.service.length})</span>
              </div>
              <div className="border rounded-lg overflow-hidden">
                <JobTable jobs={worker.service} type="service" />
              </div>
            </div>
          )}
          {worker.rental.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Truck className="h-4 w-4 text-amber-500" />
                <span className="text-sm font-medium text-slate-700">Rental Jobs ({worker.rental.length})</span>
              </div>
              <div className="border rounded-lg overflow-hidden">
                <JobTable jobs={worker.rental} type="rental" />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="p-6 space-y-6" data-testid="my-assignments-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            {isManagerOrAdmin ? "Team Assignments" : "My Assignments"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {isManagerOrAdmin 
              ? "Overview of all team assignments" 
              : "Service bookings and rentals assigned to you"}
          </p>
        </div>
        {isManagerOrAdmin && (
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-400" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]" data-testid="status-filter">
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active Jobs</SelectItem>
                <SelectItem value="all">All Jobs</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {isManagerOrAdmin && teamData ? (
          <>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-500">Total Service</CardTitle>
                <Wrench className="h-4 w-4 text-blue-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{teamData.stats.total_service}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-500">Total Rentals</CardTitle>
                <Truck className="h-4 w-4 text-amber-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-amber-600">{teamData.stats.total_rental}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-500">Team Members</CardTitle>
                <Users className="h-4 w-4 text-green-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{teamData.stats.workers_count}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-500">Unassigned</CardTitle>
                <AlertCircle className="h-4 w-4 text-red-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">
                  {teamData.stats.unassigned_service + teamData.stats.unassigned_rental}
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          <>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-500">Service Jobs</CardTitle>
                <Wrench className="h-4 w-4 text-slate-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{assignments.length}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-500">Rental Deliveries</CardTitle>
                <Truck className="h-4 w-4 text-amber-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-amber-600">{pendingDeliveries.length}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-500">Today</CardTitle>
                <Calendar className="h-4 w-4 text-blue-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">{todayAssignments.length + todayRentals.length}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-500">Confirmed</CardTitle>
                <CheckCircle className="h-4 w-4 text-green-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{confirmedCount}</div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Admin/Manager View */}
      {isManagerOrAdmin && teamData ? (
        <>
          {/* My Jobs Section */}
          {(myServiceJobs.length > 0 || myRentalJobs.length > 0) && (
            <Card className="border-green-200">
              <CardHeader className="bg-green-50">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                    <User className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <CardTitle className="text-green-800">My Jobs</CardTitle>
                    <CardDescription className="text-green-600">
                      Jobs assigned to me ({myServiceJobs.length} service, {myRentalJobs.length} rental)
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-4 space-y-4">
                {myServiceJobs.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Wrench className="h-4 w-4 text-blue-500" />
                      <span className="text-sm font-medium text-slate-700">Service Jobs ({myServiceJobs.length})</span>
                    </div>
                    <div className="border rounded-lg overflow-hidden">
                      <JobTable jobs={myServiceJobs} type="service" />
                    </div>
                  </div>
                )}
                {myRentalJobs.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Truck className="h-4 w-4 text-amber-500" />
                      <span className="text-sm font-medium text-slate-700">Rental Jobs ({myRentalJobs.length})</span>
                    </div>
                    <div className="border rounded-lg overflow-hidden">
                      <JobTable jobs={myRentalJobs} type="rental" />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Team Members Section */}
          {otherWorkers.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Users className="h-5 w-5" />
                Team Members ({otherWorkers.length})
              </h2>
              {otherWorkers.map((worker) => (
                <WorkerSection key={worker.worker_id} worker={worker} />
              ))}
            </div>
          )}

          {/* Unassigned Jobs Section */}
          {(teamData.unassigned.service.length > 0 || teamData.unassigned.rental.length > 0) && (
            <Card className="border-red-200">
              <CardHeader className="bg-red-50">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                    <UserX className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <CardTitle className="text-red-800">Unassigned Jobs</CardTitle>
                    <CardDescription className="text-red-600">
                      Jobs not yet assigned to any team member ({teamData.unassigned.service.length} service, {teamData.unassigned.rental.length} rental)
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-4 space-y-4">
                {teamData.unassigned.service.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Wrench className="h-4 w-4 text-blue-500" />
                      <span className="text-sm font-medium text-slate-700">Service Jobs ({teamData.unassigned.service.length})</span>
                    </div>
                    <div className="border rounded-lg overflow-hidden">
                      <JobTable jobs={teamData.unassigned.service} type="service" />
                    </div>
                  </div>
                )}
                {teamData.unassigned.rental.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Truck className="h-4 w-4 text-amber-500" />
                      <span className="text-sm font-medium text-slate-700">Rental Jobs ({teamData.unassigned.rental.length})</span>
                    </div>
                    <div className="border rounded-lg overflow-hidden">
                      <JobTable jobs={teamData.unassigned.rental} type="rental" />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Empty State */}
          {teamData.stats.total_service === 0 && teamData.stats.total_rental === 0 && (
            <Card>
              <CardContent className="py-12 text-center">
                <Users className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p className="text-slate-500">No team assignments found</p>
                <p className="text-sm text-slate-400">Jobs will appear here when assigned to team members</p>
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        /* Worker View - Original layout */
        <>
          {/* Today's Assignments */}
          {(todayAssignments.length > 0 || todayRentals.length > 0) && (
            <Card className="border-blue-200">
              <CardHeader className="bg-blue-50">
                <CardTitle className="text-blue-800 flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Today&apos;s Assignments ({todayAssignments.length + todayRentals.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {todayAssignments.map((assignment) => (
                    <div 
                      key={assignment.booking_id} 
                      className="border rounded-lg p-4 bg-blue-50/50 cursor-pointer hover:bg-blue-100/50 transition-colors"
                      onClick={() => navigate(`/job-card/${assignment.booking_id}`)}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Wrench className="h-4 w-4 text-blue-500" />
                            <span className="text-xs font-medium text-blue-600">SERVICE</span>
                          </div>
                          <h3 className="font-medium">{assignment.customer_name}</h3>
                          <p className="text-sm text-slate-600">{assignment.installation_name}</p>
                        </div>
                        {getStatusBadge(assignment.status)}
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2 text-slate-600">
                          <Clock className="h-4 w-4" />
                          {assignment.time_slot?.start_time} - {assignment.time_slot?.end_time}
                        </div>
                        {assignment.service_name && (
                          <div className="flex items-center gap-2 text-slate-600">
                            <Wrench className="h-4 w-4" />
                            {assignment.service_name}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {todayRentals.map((rental) => (
                    <div 
                      key={rental.rental_id} 
                      className="border rounded-lg p-4 bg-amber-50/50 cursor-pointer hover:bg-amber-100/50 transition-colors"
                      onClick={() => navigate(`/rental-job-card/${rental.rental_id}`)}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Truck className="h-4 w-4 text-amber-500" />
                            <span className="text-xs font-medium text-amber-600">RENTAL</span>
                          </div>
                          <h3 className="font-medium">{rental.customer_name}</h3>
                          <p className="text-sm text-slate-600">{rental.item_name}</p>
                        </div>
                        {getRentalStatusBadge(rental.status)}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Package className="h-4 w-4" />
                        Qty: {rental.quantity}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Rental Deliveries */}
          {rentalAssignments.length > 0 && (
            <Card className="border-amber-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Truck className="h-5 w-5 text-amber-500" />
                  Rental Deliveries ({rentalAssignments.length})
                </CardTitle>
                <CardDescription>Rental items assigned to you for delivery</CardDescription>
              </CardHeader>
              <CardContent>
                <JobTable jobs={rentalAssignments} type="rental" />
              </CardContent>
            </Card>
          )}

          {/* Service Assignments */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wrench className="h-5 w-5 text-blue-500" />
                Service Assignments ({assignments.length})
              </CardTitle>
              <CardDescription>Service bookings assigned to you</CardDescription>
            </CardHeader>
            <CardContent>
              {assignments.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Wrench className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No service assignments yet</p>
                  <p className="text-sm">You&apos;ll see service bookings assigned to you here</p>
                </div>
              ) : (
                <JobTable jobs={assignments} type="service" />
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default MyAssignments;
